/*!
 * TRACKR — first-party tracking script
 * Self-hosted ad attribution. No external dependencies. Fails silently.
 */
(function () {
  "use strict";

  // ─── Configuration ────────────────────────────────────────────────
  // The endpoint is resolved from the <script> tag's data-endpoint attribute
  // if present, otherwise it falls back to the origin that served this file.
  var COOKIE_ID = "_trkr_id";
  var COOKIE_FBC = "_trkr_fbc";
  var COOKIE_DAYS = 90;

  function resolveEndpoint() {
    try {
      var current = document.currentScript;
      if (current && current.getAttribute("data-endpoint")) {
        return current.getAttribute("data-endpoint");
      }
      var scripts = document.getElementsByTagName("script");
      for (var i = 0; i < scripts.length; i++) {
        var src = scripts[i].src || "";
        if (src.indexOf("trackr.js") !== -1) {
          var ep = scripts[i].getAttribute("data-endpoint");
          if (ep) return ep;
          var origin = src.split("/").slice(0, 3).join("/");
          return origin + "/api/track";
        }
      }
    } catch (e) {
      /* fall through */
    }
    return "/api/track";
  }

  var ENDPOINT = resolveEndpoint();

  // ─── Cookie helpers ───────────────────────────────────────────────
  // Scope cookies to the registrable root domain so a visitor's id + fbc
  // persist across subdomains (e.g. an advertorial on
  // gesundheitsartikel.sanuro.ch → the shop on sanuro.ch).
  // NOTE: the "last two labels" heuristic is correct for narello.ch /
  // sanuro.ch but would misbehave on two-part TLDs like example.co.uk. If a
  // domain like that is added later, this needs a public-suffix-aware fix.
  function getRootDomain() {
    try {
      var parts = location.hostname.split(".");
      if (parts.length <= 2) return location.hostname;
      return parts.slice(-2).join(".");
    } catch (e) {
      return location.hostname;
    }
  }

  function setCookie(name, value, days) {
    try {
      var expires = "";
      if (days) {
        var d = new Date();
        d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
        expires = "; expires=" + d.toUTCString();
      }
      var domainAttr = "";
      var root = getRootDomain();
      // Skip the domain attribute for single-label hosts (e.g. "localhost")
      // where "domain=.localhost" would cause the browser to drop the cookie.
      if (root && root.indexOf(".") !== -1) {
        domainAttr = "; domain=." + root;
      }
      document.cookie =
        name +
        "=" +
        encodeURIComponent(value) +
        expires +
        "; path=/; SameSite=Lax" +
        domainAttr;
    } catch (e) {
      /* silent */
    }
  }

  function getCookie(name) {
    try {
      var match = document.cookie.match(
        new RegExp("(?:^|; )" + name + "=([^;]*)")
      );
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  // ─── UUID (RFC4122 v4-ish, no crypto dependency required) ─────────
  function uuid() {
    try {
      if (window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID();
      }
    } catch (e) {
      /* fall through */
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // ─── Visitor id ───────────────────────────────────────────────────
  function getVisitorId() {
    var id = getCookie(COOKIE_ID);
    if (!id) {
      id = uuid();
      setCookie(COOKIE_ID, id, COOKIE_DAYS);
    } else {
      // Refresh the 90-day window on each visit.
      setCookie(COOKIE_ID, id, COOKIE_DAYS);
    }
    return id;
  }

  // ─── URL params ───────────────────────────────────────────────────
  function getParams() {
    var params = {};
    try {
      var qs = new URLSearchParams(window.location.search);
      [
        "fbclid",
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_content",
      ].forEach(function (key) {
        var val = qs.get(key);
        if (val) params[key] = val;
      });
    } catch (e) {
      /* silent */
    }
    return params;
  }

  // ─── fbclid persistence ───────────────────────────────────────────
  // Only overwrite the stored fbclid when a fresh one is present in the URL.
  function resolveFbclid(urlParams) {
    var incoming = urlParams.fbclid;
    if (incoming) {
      setCookie(COOKIE_FBC, incoming, COOKIE_DAYS);
      return incoming;
    }
    return getCookie(COOKIE_FBC);
  }

  // ─── Transport ────────────────────────────────────────────────────
  function send(type, extra) {
    try {
      var urlParams = getParams();
      var payload = {
        visitorId: getVisitorId(),
        type: type,
        pageUrl: window.location.href,
        referrer: document.referrer || null,
        fbclid: resolveFbclid(urlParams),
        utm_source: urlParams.utm_source || null,
        utm_medium: urlParams.utm_medium || null,
        utm_campaign: urlParams.utm_campaign || null,
        utm_content: urlParams.utm_content || null,
        timestamp: new Date().toISOString(),
        metadata: extra || null,
      };

      var body = JSON.stringify(payload);

      // Prefer sendBeacon for non-blocking, reliable delivery. Use a
      // text/plain Blob — a CORS-safelisted content type — so the request is
      // never preflighted (no OPTIONS). The body is still valid JSON.
      if (navigator.sendBeacon) {
        var blob = new Blob([body], { type: "text/plain" });
        if (navigator.sendBeacon(ENDPOINT, blob)) return;
      }

      // Fallback to fetch (keepalive so it survives page unloads). text/plain
      // keeps this a "simple" request too, so it also avoids preflight.
      if (window.fetch) {
        fetch(ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "text/plain" },
          body: body,
          keepalive: true,
          mode: "cors",
          credentials: "omit",
        }).catch(function () {
          /* silent */
        });
      }
    } catch (e) {
      /* never break the host page */
    }
  }

  // ─── Public API ───────────────────────────────────────────────────
  var trackr = {
    pageView: function () {
      send("page_view");
    },
    addToCart: function (data) {
      send("add_to_cart", data || {});
    },
    checkout: function () {
      send("checkout");
    },
    purchase: function (data) {
      // data: { orderId, revenue, currency, email }
      send("purchase", data || {});
    },
    // Expose for debugging / advanced use.
    _send: send,
    _visitorId: getVisitorId,
  };

  try {
    window.trackr = trackr;
  } catch (e) {
    /* silent */
  }

  // ─── Auto-fire page_view on load ──────────────────────────────────
  try {
    if (document.readyState === "complete" || document.readyState === "interactive") {
      trackr.pageView();
    } else {
      document.addEventListener("DOMContentLoaded", function () {
        trackr.pageView();
      });
    }
  } catch (e) {
    /* silent */
  }
})();

// SHOPIFY INSTALL:
// 1. Upload trackr.js to your Shopify Files
// 2. Add to theme.liquid before </head>:
//    <script src="{{ 'trackr.js' | asset_url }}" async></script>
// 3. On order confirmation page (additional_scripts in Shopify settings), add:
//    <script>
//      window.trackr && window.trackr.purchase({
//        orderId: '{{ order.id }}',
//        revenue: {{ order.total_price | divided_by: 100.0 }},
//        currency: '{{ order.currency }}',
//        email: '{{ order.email }}'
//      });
//    </script>
